import { Suspense, lazy, useMemo, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { PageKind } from '@tbbt/shared';
import { useSite } from '@/app/site-context';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { Container, Section } from '@/components/ui';
import { BrandLoader } from '@/components/layout/BrandLoader';
import { BlogPostPage } from '@/pages/public/BlogPages';
import { NotFoundPage } from '@/pages/public/NotFoundPage';
import { assertRegistryComplete, resolvePageComponent } from '@/pages/public/registry';

/**
 * The admin console is code-split.
 *
 * It ships the rich-text editor and drag-and-drop libraries, which a visitor reading a
 * blog post has no use for. Lazy-loading keeps that weight out of the public bundle
 * entirely — the marketing pages are the ones judged on load speed, and they should not
 * pay for the CMS.
 */
const AdminApp = lazy(() =>
  import('@/pages/admin/AdminApp').then((module) => ({ default: module.AdminApp })),
);

// Fails fast if a component key has no implementation. Runs once, at import time.
assertRegistryComplete();

/**
 * The application's route table.
 *
 * ## Why routes are built from data
 *
 * The public routes are not written here. They come from the API's route manifest, which
 * is derived from the `page` table. That is what delivers the client's requirement to
 * "hide, add new pages, routes and menu" without a developer: publishing a page adds a
 * route, hiding one removes it, and neither needs a deploy.
 *
 * Two consequences worth understanding before changing this file:
 *
 *  1. A hidden page genuinely 404s. It is absent from the manifest, so no route matches
 *     it — not merely unlinked from the navigation, which would leave the URL working for
 *     anyone who had it.
 *
 *  2. `/blog/:slug` is declared explicitly rather than coming from the manifest. Posts are
 *     rows in `post`, not `page`, and there could be thousands; enumerating them as routes
 *     would be absurd. One parameterised route covers them all.
 */
export function AppRouter(): ReactNode {
  return (
    <Routes>
      {/*
        The admin console sits outside PublicLayout: it has its own chrome, and it must not
        inherit the marketing header and footer.
      */}
      <Route
        path="/admin/*"
        element={
          <Suspense fallback={<AdminLoading />}>
            <AdminApp />
          </Suspense>
        }
      />

      {/*
        A short alias for the sign-in page.

        The console is deliberately unlinked — nothing in the header, footer or any page
        points at it, and it is meant to be reached by typing the URL. `/admin/login` is
        the canonical address; this exists only because it is the one people actually try.

        A redirect rather than a second copy of the form: one login page means one place
        the session, the post-login redirect and the invite-only copy are defined. Two
        would drift, and the one nobody tested would be the one someone used.
      */}
      <Route path="/login" element={<Navigate to="/admin/login" replace />} />

      <Route element={<PublicLayout />}>
        {/* Article pages: one parameterised route, not one per post. */}
        <Route path="/blog/:slug" element={<BlogPostPage />} />

        {/* Everything else is resolved against the manifest. */}
        <Route path="*" element={<ManifestRoute />} />
      </Route>
    </Routes>
  );
}

/**
 * Resolve the current path against the route manifest and render its component.
 *
 * A single catch-all rather than generating one `<Route>` per manifest entry. The reason
 * is ordering: React Router matches declared routes, and a dynamically generated list
 * would need careful sorting to stop `/services` shadowing
 * `/services/executive-search`. Matching by exact path here is unambiguous, and nested
 * paths of any depth work without special handling.
 */
function ManifestRoute(): ReactNode {
  const location = useLocation();
  const { routes, isLoading } = useSite();

  const path = location.pathname.replace(/^\/+|\/+$/g, '');

  const match = useMemo(() => routes.find((route) => route.path === path), [routes, path]);

  // The manifest has not arrived yet. Showing a skeleton rather than a 404 avoids the
  // flash of "page not found" on a slow first load — which is what would happen if this
  // fell through while `routes` was still empty.
  if (isLoading) return <PageLoading />;

  if (!match) {
    // Legacy or mistyped URLs for the blog index land here.
    if (path === 'insights') return <Navigate to="/blog" replace />;
    return <NotFoundPage />;
  }

  const Component = resolvePageComponent(
    match.kind === PageKind.CUSTOM ? null : match.componentKey,
  );

  if (!Component) {
    // A page row referencing a component key the code does not have. Reported rather
    // than rendered blank — a warning in the console with the key is what turns a
    // mysterious empty page into a two-minute fix.

    console.error(
      `No component registered for page "${match.path}" (componentKey: ${String(
        match.componentKey,
      )}). Register it in apps/web/src/pages/public/registry.tsx.`,
    );
    return <NotFoundPage />;
  }

  return <Component />;
}

function PageLoading(): ReactNode {
  return (
    <Section className="pt-32 md:pt-40">
      <Container className="flex max-w-3xl flex-col items-center">
        <BrandLoader label="Loading page…" />
      </Container>
    </Section>
  );
}

function AdminLoading(): ReactNode {
  return (
    <div className="grid min-h-dvh place-items-center bg-surface-wash">
      <BrandLoader size="lg" label="Loading admin console…" />
    </div>
  );
}
